import React, { Fragment } from "react";

import * as Icon from "@common/components/DetailsPane/icons";
import { ErrorStateView } from "@common/components/PageStateView/components/ErrorStateView";
import { Spinner } from "@common/components/Spinner/Spinner";
import Tooltip from "@common/components/Tooltip/Tooltip";
import { useCustomElementInjection } from "@common/hooks/useCustomElementInjection";
import { Flex } from "@common/styles/reusable/Flex/Flex.styles";
import NavigationCustomElement from "@public/layouts/components/NavigationCustomElement/NavigationCustomElement";
import { ELEMENT_CONTAINER_ID, NAV_LIST } from "@public/layouts/components/PageNavigation";
import { ActivityHistoryTable } from "@public/pages/shared/components/ActivityHistoryTable/ActivityHistoryTable";
import { RoutePaths } from "@public/router/config/routePaths";
import { generatePath, useNavigate, useParams } from "react-router-dom";

import * as Styled from "./UserFeedback.styles";
import useUserFeedbackDetailsApiConsumer from "../consumers/useUserFeedbackDetailsApiConsumer";

export default function UserFeedbackActivityHistoryPageView() {
  const navigate = useNavigate();
  const params = useParams<{ issueId: string }>();

  const consumer = useUserFeedbackDetailsApiConsumer({
    issueId: params.issueId,
  });

  const CustomElement = consumer.userFeedbackDetails ? (
    <Fragment>
      <NavigationCustomElement>
        <Styled.NavigationTitle $isButton={false}>
          <div>
            <p>{consumer.userFeedbackDetails?.title ?? "No description"}</p>
          </div>
        </Styled.NavigationTitle>
        <Styled.NavigationTitle $isButton={false}>
          <Flex>
            <div>
              <Tooltip
                text={"See ticket details"}
                position={"bottom"}
              >
                <Styled.NavbarNavigation
                  onClick={() =>
                    navigate(
                      generatePath(RoutePaths.USER_FEEDBACK_DETAILS, {
                        issueId: params.issueId ?? null,
                      }),
                    )
                  }
                >
                  <Icon.SendIcon
                    height={20}
                    width={20}
                  />
                </Styled.NavbarNavigation>
              </Tooltip>
            </div>
            <p>Activity history</p>
            <div>
              <Tooltip
                text={!consumer.userFeedbackDetails.log ? "No logs to download" : "Download as .log file"}
                position="bottom"
              >
                <Styled.DownloadIcon
                  disabled={!consumer.userFeedbackDetails.log}
                  onClick={() => {
                    if (consumer.userFeedbackDetails?.log)
                      consumer.downloadActivityHistory(consumer.userFeedbackDetails?.id);
                  }}
                  $disabled={!consumer.userFeedbackDetails.log}
                />
              </Tooltip>
            </div>
          </Flex>
        </Styled.NavigationTitle>
      </NavigationCustomElement>
    </Fragment>
  ) : (
    <Fragment />
  );

  useCustomElementInjection({
    parentElementId: NAV_LIST,
    element: CustomElement,
    containerId: ELEMENT_CONTAINER_ID,
    tagName: "li",
  });

  if (consumer.error) {
    return (
      <ErrorStateView
        title="There was an error loading ticket. Try refreshing."
        subtitle="The link you followed may be broken, or the ticket may have been removed."
      />
    );
  }

  if (!params.issueId) return <Fragment />;

  if (!consumer.userFeedbackDetails) {
    return <Spinner />;
  }

  return (
    <ActivityHistoryTable
      ticketId={params.issueId}
      hasLogs={Boolean(consumer.userFeedbackDetails?.log)}
      type="user_feedback"
    />
  );
}
